import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, X } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, MaskedLine, ScrollMaskedLine } from "@/components/Reveal";
import { ExaminedSequence } from "@/components/ExaminedSequence";
import { SESSIONS, getSession } from "@/data/sessions";
import { usePageTitle } from "@/lib/usePageTitle";

/* ------------------------------------------------------------------ */

function SeriesNav({ current }) {
  return (
    <div className="flex items-center gap-5" data-testid="series-nav">
      <span className="text-[10px] tracking-[0.25em] uppercase text-fog">Series</span>
      <span className="h-px w-6 bg-line" aria-hidden="true" />
      {SESSIONS.map((s) => (
        <Link
          key={s.slug}
          to={`/session/${s.slug}`}
          data-testid={`series-nav-${s.n}`}
          className={`group/sn relative text-sm tracking-widest transition-colors duration-300 ${
            s.slug === current ? "text-ink" : "text-fog hover:text-ink"
          }`}
        >
          {s.n}
          {s.slug === current && (
            <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-ember" aria-hidden="true" />
          )}
        </Link>
      ))}
    </div>
  );
}

function Eyebrow({ children, testid }) {
  return (
    <Reveal>
      <p className="text-xs tracking-[0.25em] uppercase text-fog" data-testid={testid}>
        {children}
      </p>
    </Reveal>
  );
}


/* In-page slide viewer: the presentation stays on the page */
function SlideViewer({ viewer, onClose, onNav }) {
  const { slides, index } = viewer;
  const src = slides[index].replace("rs=w:600", "rs=w:1400");

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onNav]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
      className="fixed inset-0 z-[80] bg-bone/95 backdrop-blur-sm flex flex-col"
      data-testid="slide-viewer"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Slide ${index + 1} of ${slides.length}`}
    >
      <div className="flex items-center justify-between px-6 md:px-12 py-5">
        <span className="text-[11px] tracking-[0.25em] uppercase text-fog" data-testid="slide-viewer-counter">
          {String(index + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
        </span>
        <button
          onClick={onClose}
          data-testid="slide-viewer-close"
          aria-label="Close presentation"
          className="group inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-fog hover:text-ink transition-colors duration-300"
        >
          Close
          <X size={15} className="transition-transform duration-300 group-hover:rotate-90" />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 md:px-24 pb-6 min-h-0" onClick={(e) => e.stopPropagation()}>
        <motion.img
          key={src}
          src={src}
          alt={`Session slide ${index + 1}`}
          data-testid="slide-viewer-image"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-full max-h-full object-contain shadow-[0_24px_80px_-30px_rgba(15,15,15,0.35)]"
        />
      </div>
      <div className="flex items-center justify-center gap-10 pb-8" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={() => onNav(-1)}
          disabled={index === 0}
          data-testid="slide-viewer-prev"
          aria-label="Previous slide"
          className="group inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-fog hover:text-ink transition-colors duration-300 disabled:opacity-30 disabled:pointer-events-none"
        >
          <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
          Prev
        </button>
        <span className="flex gap-1.5" aria-hidden="true">
          {slides.map((_, d) => (
            <span key={d} className={`w-1 h-1 rounded-full transition-colors duration-300 ${d === index ? "bg-ember" : "bg-ink/15"}`} />
          ))}
        </span>
        <button
          onClick={() => onNav(1)}
          disabled={index === slides.length - 1}
          data-testid="slide-viewer-next"
          aria-label="Next slide"
          className="group inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-fog hover:text-ink transition-colors duration-300 disabled:opacity-30 disabled:pointer-events-none"
        >
          Next
          <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </div>
    </motion.div>
  );
}

export default function SessionPage() {
  const { slug } = useParams();
  const session = getSession(slug);
  const [showSlides, setShowSlides] = useState(false);
  const [viewer, setViewer] = useState(null);
  usePageTitle(
    session ? `Research Dialogue ${session.n}: ${session.title} | Mentheris` : "Research Dialogues | Mentheris",
    session ? session.lede : undefined
  );

  if (!session) return <Navigate to="/research-dialogues" replace />;

  const nextSession = session.next ? getSession(session.next.slug) : null;
  const navSlide = (dir) =>
    setViewer((v) => ({
      ...v,
      index: Math.min(v.slides.length - 1, Math.max(0, v.index + dir)),
    }));

  return (
    <main className="pt-24 md:pt-32" data-testid={`session-page-${session.n}`}>
      {/* ---------------- HERO ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 pb-16 md:pb-20">
        <MaskedLine delay={0.05}>
          <Link
            to="/research-dialogues"
            data-testid="session-back-link"
            className="group inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-fog hover:text-ink transition-colors duration-300"
          >
            <ArrowLeft size={13} className="transition-transform duration-300 group-hover:-translate-x-1" />
            Research Dialogues
          </Link>
        </MaskedLine>

        <div className="mt-10 max-w-4xl">
          <div>
            <MaskedLine delay={0.2}>
              <span className="text-xs tracking-[0.3em] uppercase text-fog" data-testid="session-label">
                Mentheris Research Dialogues · {session.n}
                <span className="text-ember"> / {String(SESSIONS.length).padStart(2, "0")}</span>
              </span>
            </MaskedLine>
            <h1 className="font-serif font-medium tracking-tighter leading-[1.02] text-4xl md:text-6xl mt-8" data-testid="session-headline">
              {session.headline.map((line, i) => (
                <MaskedLine key={i} delay={0.35 + i * 0.15}>
                  {i === session.headline.length - 1 ? (
                    <span className="italic text-ember">{line}</span>
                  ) : (
                    line
                  )}
                </MaskedLine>
              ))}
            </h1>
            <Reveal delay={0.85} className="mt-8 max-w-xl">
              <p className="text-base md:text-lg text-fog leading-relaxed">{session.lede}</p>
            </Reveal>
            <Reveal delay={1.0} className="mt-10">
              <dl className="flex flex-wrap gap-x-10 gap-y-4" data-testid="session-meta">
                {session.meta.map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-[10px] tracking-[0.25em] uppercase text-fog/70">{k}</dt>
                    <dd className="mt-1 text-sm text-ink/80">{v}</dd>
                  </div>
                ))}
                <div>
                  <dt className="text-[10px] tracking-[0.25em] uppercase text-fog/70">Status</dt>
                  <dd className="mt-1 text-sm text-ink/80">{session.statusLabel}</dd>
                </div>
              </dl>
            </Reveal>
            {session.register && (
              <Reveal delay={1.15} className="mt-10">
                <a
                  href={session.register.href}
                  data-testid="session-register-btn"
                  className="group inline-flex items-center gap-3 bg-ink text-bone px-8 py-4 text-sm tracking-wide hover:bg-ember transition-colors duration-300"
                >
                  {session.register.label}
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              </Reveal>
            )}
            {session.status === "soon" && (
              <Reveal delay={1.15} className="mt-10">
                <p className="text-sm text-fog italic" data-testid="session-soon-note">
                  Registration opens soon.
                </p>
              </Reveal>
            )}
            <Reveal delay={1.25} className="mt-12">
              <SeriesNav current={session.slug} />
            </Reveal>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.9 }}
          className="mt-16 flex items-center gap-3 text-fog"
          aria-hidden="true"
        >
          <motion.span
            className="block w-px h-10 bg-fog/50 origin-top"
            animate={{ scaleY: [1, 0.4, 1] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          />
          <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        </motion.div>
      </section>

      {/* ---------------- THE QUESTION BEHIND THE SESSION ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-24" data-testid="question-section">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <Eyebrow testid="question-eyebrow">The question behind the session</Eyebrow>
          </div>
          <div className="md:col-span-9">
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight">
              <ScrollMaskedLine>Where the inquiry began.</ScrollMaskedLine>
            </h2>
            <Reveal delay={0.15} className="mt-8 max-w-2xl space-y-5">
              {session.context.map((p, i) => (
                <p key={i} className="text-fog leading-relaxed">{p}</p>
              ))}
            </Reveal>
            <div className="mt-14 md:mt-16 md:pl-10">
              <Reveal>
                <p className="text-[10px] tracking-[0.3em] uppercase text-ember mb-5">The question</p>
              </Reveal>
              <h3 className="font-serif text-2xl md:text-4xl leading-[1.25] tracking-tight max-w-3xl" data-testid="central-question">
                <ScrollMaskedLine delay={0.1}>{session.question}</ScrollMaskedLine>
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- WHAT WE EXAMINED ---------------- */}
      {session.examined && (
        <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-24" data-testid="examined-section">
          <Eyebrow testid="examined-eyebrow">What we examined</Eyebrow>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mt-6 mb-16 md:mb-20 max-w-3xl">
            <ScrollMaskedLine>Following the question through the system.</ScrollMaskedLine>
          </h2>
          <ExaminedSequence stages={session.examined} />
        </section>
      )}

      {/* ---------------- CENTRAL CONCEPT ---------------- */}
      {session.framework && (
        <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-24" data-testid="framework-section">
          <div className="max-w-3xl">
            <div>
              <Eyebrow testid="framework-eyebrow">Central concept</Eyebrow>
              <h2 className="font-serif font-medium tracking-tighter leading-[1.0] text-4xl md:text-6xl mt-6">
                <ScrollMaskedLine>{session.framework.name[0]}</ScrollMaskedLine>
                <ScrollMaskedLine delay={0.15}>
                  <span className="italic text-ember">{session.framework.name[1]}</span>
                </ScrollMaskedLine>
              </h2>
              <Reveal delay={0.2} className="mt-8 max-w-xl">
                <p className="text-lg md:text-xl leading-relaxed text-ink/85" data-testid="framework-definition">
                  {session.framework.definition}
                </p>
              </Reveal>
              <Reveal delay={0.3} className="mt-8 max-w-xl">
                <p className="text-sm text-fog leading-relaxed">
                  {session.framework.foundation}{" "}
                  <a
                    href={session.framework.paperHref}
                    data-testid="framework-paper-link"
                    className="link-editorial text-ink"
                  >
                    Read the paper
                  </a>
                </p>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* ---------------- IDEAS EXAMINED ---------------- */}
      {session.ideas && (
        <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-24" data-testid="ideas-section">
          <Eyebrow testid="ideas-eyebrow">Ideas examined</Eyebrow>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mt-6 mb-14 md:mb-16">
            <ScrollMaskedLine>Ideas the session placed under examination.</ScrollMaskedLine>
          </h2>
          <div className="grid md:grid-cols-3 gap-10 md:gap-12">
            {session.ideas.map((idea, i) => (
              <Reveal key={i} delay={i * 0.12}>
                <div data-testid={`idea-${i}`}>
                  <span className="block w-8 h-px bg-ember mb-6" aria-hidden="true" />
                  <h3 className="font-serif text-xl md:text-2xl leading-snug">{idea.statement}</h3>
                  <p className="mt-4 text-sm text-fog leading-relaxed">{idea.explanation}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* ---------------- OPEN QUESTIONS ---------------- */}
      {session.openQuestions && (
        <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-28" data-testid="open-questions-section">
          <Eyebrow testid="open-questions-eyebrow">Open questions</Eyebrow>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mt-6 mb-14 md:mb-20">
            <ScrollMaskedLine>The questions that remain.</ScrollMaskedLine>
          </h2>
          <div className="space-y-10 md:space-y-14 max-w-4xl">
            {session.openQuestions.map((q, i) => (
              <div key={i} className="grid md:grid-cols-12 gap-4 items-baseline" data-testid={`open-question-${i}`}>
                <span className="md:col-span-2 text-[11px] tracking-[0.25em] uppercase text-fog">
                  0{i + 1}
                </span>
                <h3 className="md:col-span-10 font-serif text-2xl md:text-4xl leading-[1.25] tracking-tight text-ink/85">
                  <ScrollMaskedLine delay={i * 0.1}>{q}</ScrollMaskedLine>
                </h3>
              </div>
            ))}
          </div>
          <Reveal delay={0.3} className="mt-14">
            <p className="text-sm text-fog italic">Research continues.</p>
          </Reveal>
        </section>
      )}

      {/* ---------------- SESSION MATERIALS ---------------- */}
      {session.materials && (
        <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-24" data-testid="materials-section">
          <Eyebrow testid="materials-eyebrow">Session materials</Eyebrow>
          <div className="mt-8 max-w-3xl">
            {session.materials.map((m, i) =>
              m.type === "slides" ? (
                <div key={i}>
                  <button
                    onClick={() => setShowSlides((v) => !v)}
                    data-testid="materials-slides-toggle"
                    className="group relative flex w-full items-baseline justify-between gap-6 py-6 text-left"
                    aria-expanded={showSlides}
                  >
                    <span className="absolute left-0 bottom-0 h-[2px] w-0 bg-leaf transition-all duration-500 ease-out group-hover:w-full" aria-hidden="true" />
                    <span className="flex items-baseline gap-6">
                      <span className="text-[11px] tracking-[0.2em] text-fog">0{i + 1}</span>
                      <span>
                        <span className="font-serif text-xl md:text-2xl transition-transform duration-500 group-hover:translate-x-1 inline-block">
                          {m.label}
                        </span>
                        <span className="block mt-1 text-xs text-fog">{m.note}</span>
                      </span>
                    </span>
                    <ArrowUpRight
                      size={18}
                      className={`shrink-0 text-fog transition-transform duration-500 ${showSlides ? "rotate-90" : "group-hover:translate-x-1 group-hover:-translate-y-1"}`}
                    />
                  </button>
                  {showSlides && (
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                      className="grid grid-cols-2 md:grid-cols-5 gap-3 pb-8"
                      data-testid="slides-gallery"
                    >
                      {m.slides.map((src, j) => (
                        <button
                          key={j}
                          onClick={() => setViewer({ slides: m.slides, index: j })}
                          data-testid={`slide-${j}`}
                          className="block group/slide cursor-zoom-in"
                          aria-label={`Open slide ${j + 1}`}
                        >
                          <img
                            src={src}
                            alt={`Session slide ${j + 1}`}
                            loading="lazy"
                            className="w-full aspect-[4/3] object-cover object-top opacity-90 group-hover/slide:opacity-100 transition-opacity duration-300"
                          />
                        </button>
                      ))}
                    </motion.div>
                  )}
                </div>
              ) : (
                <a
                  key={i}
                  href={m.href}
                  data-testid={`materials-link-${i}`}
                  className="group relative flex items-baseline justify-between gap-6 py-6"
                >
                  <span className="absolute left-0 bottom-0 h-[2px] w-0 bg-leaf transition-all duration-500 ease-out group-hover:w-full" aria-hidden="true" />
                  <span className="flex items-baseline gap-6">
                    <span className="text-[11px] tracking-[0.2em] text-fog">0{i + 1}</span>
                    <span>
                      <span className="font-serif text-xl md:text-2xl transition-transform duration-500 group-hover:translate-x-1 inline-block">
                        {m.label}
                      </span>
                      <span className="block mt-1 text-xs text-fog">{m.note}</span>
                    </span>
                  </span>
                  <ArrowUpRight size={18} className="shrink-0 text-fog transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              )
            )}
          </div>
        </section>
      )}

      {/* ---------------- CONTINUE THE INQUIRY ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-24" data-testid="continue-section">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <Eyebrow testid="continue-eyebrow">Continue the inquiry</Eyebrow>
          </div>
          <div className="md:col-span-9">
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight max-w-3xl">
              <ScrollMaskedLine>The conversation is part of a larger body of work.</ScrollMaskedLine>
            </h2>
            <Reveal delay={0.15} className="mt-6 max-w-2xl">
              <p className="text-fog leading-relaxed">
                Research Dialogues are not isolated events. They create spaces where Mentheris
                research can be questioned, expanded and connected to other perspectives.
              </p>
            </Reveal>
            <div className="mt-12 max-w-3xl">
              <a
                href="https://ssrn.com/abstract=6426239"
                data-testid="continue-paper-link"
                className="group relative flex items-baseline justify-between gap-6 py-6"
              >
                <span className="absolute left-0 bottom-0 h-[2px] w-0 bg-leaf transition-all duration-500 ease-out group-hover:w-full" aria-hidden="true" />
                <span>
                  <span className="block text-[10px] tracking-[0.25em] uppercase text-fog mb-2">Research paper</span>
                  <span className="font-serif text-xl md:text-2xl leading-snug transition-transform duration-500 group-hover:translate-x-1 inline-block">
                    Technology-Human Disequilibrium
                  </span>
                </span>
                <span className="flex items-center gap-1.5 text-xs tracking-wide text-ink shrink-0">
                  Explore
                  <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </a>
              <Link
                to="/research"
                data-testid="continue-research-link"
                className="group relative flex items-baseline justify-between gap-6 py-6"
              >
                <span className="absolute left-0 bottom-0 h-[2px] w-0 bg-leaf transition-all duration-500 ease-out group-hover:w-full" aria-hidden="true" />
                <span>
                  <span className="block text-[10px] tracking-[0.25em] uppercase text-fog mb-2">Research</span>
                  <span className="font-serif text-xl md:text-2xl leading-snug transition-transform duration-500 group-hover:translate-x-1 inline-block">
                    We study what change sets in motion
                  </span>
                </span>
                <span className="flex items-center gap-1.5 text-xs tracking-wide text-ink shrink-0">
                  Explore
                  <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
              <Link
                to="/research-dialogues"
                data-testid="continue-series-link"
                className="group relative flex items-baseline justify-between gap-6 py-6"
              >
                <span className="absolute left-0 bottom-0 h-[2px] w-0 bg-leaf transition-all duration-500 ease-out group-hover:w-full" aria-hidden="true" />
                <span>
                  <span className="block text-[10px] tracking-[0.25em] uppercase text-fog mb-2">Series</span>
                  <span className="font-serif text-xl md:text-2xl leading-snug transition-transform duration-500 group-hover:translate-x-1 inline-block">
                    All Research Dialogues
                  </span>
                </span>
                <span className="flex items-center gap-1.5 text-xs tracking-wide text-ink shrink-0">
                  Explore
                  <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- THE INQUIRY CONTINUES ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 pt-20 md:pt-24 pb-24 md:pb-32" data-testid="next-section">
        <Eyebrow testid="next-eyebrow">The inquiry continues</Eyebrow>
        <div className="mt-10 max-w-4xl">
          <div>
            {nextSession ? (
              <>
                <div className="flex items-baseline gap-6">
                  <span className="font-serif text-6xl md:text-8xl leading-none text-ink/15">{nextSession.n}</span>
                </div>
                <h2 className="font-serif text-2xl md:text-4xl tracking-tight leading-tight mt-6 max-w-2xl" data-testid="next-session-title">
                  <ScrollMaskedLine>{nextSession.title}</ScrollMaskedLine>
                </h2>
                <Reveal delay={0.15} className="mt-5 max-w-xl">
                  <p className="text-fog leading-relaxed">{nextSession.lede}</p>
                </Reveal>
                <Reveal delay={0.25} className="mt-10 flex flex-wrap items-center gap-8">
                  <Link
                    to={`/session/${nextSession.slug}`}
                    data-testid="next-session-cta"
                    className="group inline-flex items-center gap-3 bg-ink text-bone px-8 py-4 text-sm tracking-wide hover:bg-ember transition-colors duration-300"
                  >
                    Continue to Dialogue {nextSession.n}
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                  {session.prev && (
                    <Link
                      to={`/session/${session.prev.slug}`}
                      data-testid="prev-session-link"
                      className="group inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-fog hover:text-ink transition-colors duration-300"
                    >
                      <ArrowLeft size={13} className="transition-transform duration-300 group-hover:-translate-x-1" />
                      Previous Dialogue
                    </Link>
                  )}
                </Reveal>
              </>
            ) : (
              <>
                <h2 className="font-serif text-2xl md:text-4xl tracking-tight leading-tight max-w-2xl">
                  <ScrollMaskedLine>More dialogues are in development.</ScrollMaskedLine>
                </h2>
                <Reveal delay={0.2} className="mt-10 flex flex-wrap items-center gap-8">
                  <Link
                    to="/research-dialogues"
                    data-testid="next-series-cta"
                    className="group inline-flex items-center gap-3 bg-ink text-bone px-8 py-4 text-sm tracking-wide hover:bg-ember transition-colors duration-300"
                  >
                    Back to the series
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                  {session.prev && (
                    <Link
                      to={`/session/${session.prev.slug}`}
                      data-testid="prev-session-link"
                      className="group inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-fog hover:text-ink transition-colors duration-300"
                    >
                      <ArrowLeft size={13} className="transition-transform duration-300 group-hover:-translate-x-1" />
                      Previous Dialogue
                    </Link>
                  )}
                </Reveal>
              </>
            )}
          </div>
        </div>
      </section>

      {viewer && (
        <SlideViewer viewer={viewer} onClose={() => setViewer(null)} onNav={navSlide} />
      )}
    </main>
  );
}
